<script setup lang="ts">
import { computed } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { AppSingleSelectType } from 'libs/ui/src/lib/components/selects/models/single.model';

import { AppSingleSelect, AppSvgIcon, AppLinkButton } from '@libs/ui';
import type {
  AppSingleSelectOption,
  AppSingleSelectFontVariant,
  AppSvgIconType,
} from '@libs/ui';

import { getSelectedOptionForSuggestionType } from './SuggestionTypeSelector.helper';
import {
  SuggestionType,
  InfoIconPosition,
  SuggestionTypeSelectorSize,
} from './SuggestionTypeSelector.model';

const { t } = i18n.global;
interface SuggestionTypeSelectorProps {
  suggestionType: SuggestionType;
  fontVariant: AppSingleSelectFontVariant;
  appSelectType: AppSingleSelectType;
  iconPosition: 'right' | 'left';
  showInfoIcon: InfoIconPosition;
  size: SuggestionTypeSelectorSize;
  iconName?: AppSvgIconType;
  showWhyWeSuggestInside?: boolean;
  isWrapperScrolling?: boolean;
  sortIcon?: AppSvgIconType;
  showChevronIcon?: boolean;
  showLabel?: boolean;
}
const props = withDefaults(defineProps<SuggestionTypeSelectorProps>(), {
  iconName: 'infoCircle',
  showLabel: true,
  showChevronIcon: true,
});
interface SuggestionTypeSelectorEmits {
  (eventName: 'suggestion-type-selected', suggestionType: SuggestionType): void;
  (eventName: 'show-info'): void;
}

const emit = defineEmits<SuggestionTypeSelectorEmits>();

const selectedSuggestionType = computed(() =>
  getSelectedOptionForSuggestionType(
    SUGGESTION_TYPE_OPTIONS,
    props.suggestionType
  )
);

function onSuggestionTypeSelect(
  option: AppSingleSelectOption<SuggestionType>
): void {
  emit('suggestion-type-selected', option.id);
}

const SUGGESTION_TYPE_OPTIONS: AppSingleSelectOption<SuggestionType>[] = [
  {
    id: SuggestionType.Cheapest,
    label: t('planner.lowest_price'),
  },
  {
    id: SuggestionType.MostPopular,
    label: t('planner.recommended'),
  },
];

const insideIcon = computed(() =>
  props.showInfoIcon === InfoIconPosition.Inside ? props.iconName : undefined
);
</script>

<template>
  <div class="suggestion-type-selector" :class="[`${props.iconPosition}-icon`]">
    <AppSvgIcon
      v-if="
        selectedSuggestionType?.id === SuggestionType.MostPopular &&
        props.showInfoIcon === InfoIconPosition.Outside
      "
      class="info-icon"
      name="infoCircle"
      size="18"
      @click="emit('show-info')"
    />
    <AppSingleSelect
      :type="props.appSelectType"
      :font-variant="props.fontVariant"
      :options="SUGGESTION_TYPE_OPTIONS"
      :size="
        props.size !== SuggestionTypeSelectorSize.Normal
          ? props.size
          : undefined
      "
      :selected="selectedSuggestionType"
      :left-side-icon="insideIcon"
      :right-side-icon="sortIcon"
      right-side-icon-size="18"
      :show-chevrons="props.showChevronIcon"
      :is-wrapper-scrolling="props.isWrapperScrolling"
      @select="onSuggestionTypeSelect"
      @left-icon-click="emit('show-info')"
    >
      <template #footer>
        <div v-if="showWhyWeSuggestInside" class="why-we-suggest">
          <span class="header">
            {{ $t('planner.suggestion_modal.header') }}
          </span>
          <AppLinkButton
            :text="t('planner.read_more')"
            size="s"
            @click="emit('show-info')"
          />
        </div>
      </template>
    </AppSingleSelect>
  </div>
</template>

<style scoped lang="scss">
.suggestion-type-selector {
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
  &.right-icon {
    flex-direction: row;
  }
  &.left-icon {
    flex-direction: row-reverse;
  }
  .info-icon {
    cursor: pointer;
  }
}
</style>

<style lang="scss">
.why-we-suggest {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-8);
  padding: var(--spacing-12) var(--spacing-16);
  border-top: 1px solid var(--color-scrollbar);
  .header {
    @include font-label-s-bold;
  }
}
</style>
